<template>
  <layouts></layouts>
  <student-breadcrumb :title="title" :text="text" :text1="text1" />

  <div class="page-content course-sec">
    <div class="container">
      <div class="row align-items-center" style="margin-top: 50px;">
        <div class="col-md-12">
          <div class="add-course-header">
            <h2>Import Quiz from Excel</h2>
            <div class="add-course-btns">
              <ul class="nav">
                <li>
                  <router-link to="/caduceus/quizzes" class="btn btn-black">
                    Back to Quizzes
                  </router-link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <div class="card">
            <div class="widget-set">
              <div class="widget-content">
                <div v-if="!previewData" class="add-course-info">
                  <div class="add-course-inner-header">
                    <h4>Upload Excel File</h4>
                  </div>

                  <div class="add-course-form">
                    <div class="form-group">
                      <label class="add-course-label">Select Course *</label>
                      <select
                        v-model="selectedCourseId"
                        class="form-select"
                        required
                      >
                        <option :value="null">Select a course</option>
                        <option
                          v-for="course in courses"
                          :key="course.id"
                          :value="course.id"
                        >
                          {{ course.title }}
                        </option>
                      </select>
                    </div>

                    <div class="form-group">
                      <label class="add-course-label">Excel File *</label>
                      <div
                        class="upload-zone"
                        :class="{ 'drag-over': isDragging }"
                        @drop.prevent="handleDrop"
                        @dragover.prevent="isDragging = true"
                        @dragleave.prevent="isDragging = false"
                      >
                        <input
                          ref="fileInputRef"
                          type="file"
                          accept=".xlsx,.xls"
                          @change="handleFileSelect"
                          style="display: none"
                        />

                        <div v-if="!selectedFile" class="upload-placeholder">
                          <i class="bx bx-cloud-upload"></i>
                          <p>Drag and drop your Excel file here</p>
                          <p class="text-muted">or</p>
                          <button
                            @click="$refs.fileInputRef.click()"
                            class="btn btn-primary"
                            type="button"
                          >
                            Browse Files
                          </button>
                          <p class="text-muted mt-3">
                            <small>Supported formats: .xlsx, .xls (Max 5MB)</small>
                          </p>
                        </div>

                        <div v-else class="upload-file-info">
                          <i class="bx bx-file"></i>
                          <div class="file-details">
                            <p class="file-name">{{ selectedFile.name }}</p>
                            <p class="file-size text-muted">
                              {{ formatFileSize(selectedFile.size) }}
                            </p>
                          </div>
                          <button
                            @click="clearFile"
                            class="btn btn-sm btn-outline-danger"
                            type="button"
                          >
                            <i class="bx bx-x"></i>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div class="text-end">
                      <button
                        @click="handlePreview"
                        class="btn btn-primary"
                        :disabled="!selectedFile || !selectedCourseId || uploading"
                      >
                        <span v-if="uploading" class="spinner-border spinner-border-sm me-1"></span>
                        Upload and Validate
                      </button>
                    </div>
                  </div>
                </div>

                <div v-else class="add-course-info">
                  <div class="add-course-inner-header">
                    <h4>Preview and Validation</h4>
                  </div>

                  <div v-if="previewData.errors && previewData.errors.length > 0" class="alert alert-danger">
                    <h5><i class="bx bx-error-circle me-2"></i>Validation Errors</h5>
                    <ul class="mb-0">
                      <li v-for="(error, index) in previewData.errors" :key="index">
                        {{ error }}
                      </li>
                    </ul>
                  </div>

                  <div v-if="previewData.valid" class="preview-content">
                    <div class="alert alert-success">
                      <i class="bx bx-check-circle me-2"></i>
                      File validated successfully! Review the preview below.
                    </div>

                    <div class="preview-summary mb-4">
                      <h5>Quiz Details</h5>
                      <div class="row">
                        <div class="col-md-6">
                          <p><strong>Title:</strong> {{ previewData.preview?.title || previewData.parsedData?.quizTitle }}</p>
                          <p><strong>Description:</strong> {{ previewData.preview?.description || 'N/A' }}</p>
                        </div>
                        <div class="col-md-6">
                          <p><strong>Total Questions:</strong> {{ previewData.preview?.questions?.length || 0 }}</p>
                          <p><strong>Course:</strong> {{ getCourseName(selectedCourseId) }}</p>
                        </div>
                      </div>
                    </div>

                    <h5 class="mb-3">Questions Preview</h5>
                    <div class="table-responsive">
                      <table class="table table-bordered">
                        <thead>
                          <tr>
                            <th style="width: 5%">#</th>
                            <th style="width: 35%">Question</th>
                            <th style="width: 40%">Options</th>
                            <th style="width: 20%">Correct Answer(s)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(question, index) in previewData.preview.questions"
                            :key="index"
                          >
                            <td>{{ index + 1 }}</td>
                            <td>{{ question.text }}</td>
                            <td>
                              <ol type="A" class="mb-0">
                                <li v-for="(opt, optIndex) in question.options" :key="optIndex">
                                  {{ opt.text }}
                                </li>
                              </ol>
                            </td>
                            <td>
                              <span
                                v-for="(opt, optIndex) in question.options.filter(o => o.isCorrect)"
                                :key="optIndex"
                                class="badge bg-success me-1"
                              >
                                {{ String.fromCharCode(65 + question.options.indexOf(opt)) }}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div class="text-end mt-4">
                    <button
                      @click="cancelImport"
                      class="btn btn-secondary me-2"
                    >
                      Cancel
                    </button>
                    <button
                      v-if="previewData.valid"
                      @click="confirmImport"
                      class="btn btn-success"
                      :disabled="importing"
                    >
                      <span v-if="importing" class="spinner-border spinner-border-sm me-1"></span>
                      Confirm Import
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <layouts1></layouts1>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import QuizService from '@/services/quiz.service';
import ApiService from '@/services/ApiService';
import Swal from 'sweetalert2';

const router = useRouter();

const title = ref('Import Quiz');
const text = ref('Home');
const text1 = ref('Import');

const courses = ref([]);
const selectedCourseId = ref(null);
const selectedFile = ref(null);
const isDragging = ref(false);
const uploading = ref(false);
const importing = ref(false);
const previewData = ref(null);
const fileInputRef = ref(null);

const fetchCourses = async () => {
  try {
    const response = await ApiService.get('/courses/me');
    courses.value = response.data || [];
  } catch (err) {
    console.error('Error fetching courses:', err);
  }
};

const getCourseName = (courseId) => {
  const course = courses.value.find(c => c.id === courseId);
  return course?.title || 'Unknown';
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

const validateFile = (file) => {
  const validTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel'
  ];
  const maxSize = 5 * 1024 * 1024;

  if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
    Swal.fire({
      icon: 'error',
      title: 'Invalid File Type',
      text: 'Please upload an Excel file (.xlsx or .xls)'
    });
    return false;
  }

  if (file.size > maxSize) {
    Swal.fire({
      icon: 'error',
      title: 'File Too Large',
      text: 'File size must not exceed 5MB'
    });
    return false;
  }

  return true;
};

const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (file && validateFile(file)) {
    selectedFile.value = file;
  }
};

const handleDrop = (event) => {
  isDragging.value = false;
  const file = event.dataTransfer.files[0];
  if (file && validateFile(file)) {
    selectedFile.value = file;
  }
};

const clearFile = () => {
  selectedFile.value = null;
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
};

const handlePreview = async () => {
  if (!selectedFile.value || !selectedCourseId.value) {
    Swal.fire({
      icon: 'warning',
      title: 'Missing Information',
      text: 'Please select both a course and a file'
    });
    return;
  }

  uploading.value = true;

  try {
    const response = await QuizService.previewExcelImport(
      selectedFile.value,
      selectedCourseId.value
    );

    previewData.value = response.data;

    if (!previewData.value.valid) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Failed',
        text: 'Please fix the errors and try again'
      });
    }
  } catch (err) {
    console.error('Error previewing file:', err);
    Swal.fire({
      icon: 'error',
      title: 'Upload Failed',
      text: err.response?.data?.message || 'Failed to upload file'
    });
  } finally {
    uploading.value = false;
  }
};

const cancelImport = () => {
  previewData.value = null;
  clearFile();
};

const confirmImport = async () => {
  importing.value = true;

  try {
    const response = await QuizService.confirmExcelImport({
      courseId: selectedCourseId.value,
      parsedData: previewData.value.parsedData
    });

    Swal.fire({
      icon: 'success',
      title: 'Import Successful',
      text: 'Quiz has been imported successfully'
    });

    router.push(`/caduceus/quizzes/${response.data.id}/edit`);
  } catch (err) {
    console.error('Error confirming import:', err);
    Swal.fire({
      icon: 'error',
      title: 'Import Failed',
      text: err.response?.data?.message || 'Failed to import quiz'
    });
  } finally {
    importing.value = false;
  }
};

onMounted(() => {
  fetchCourses();
});
</script>

<style scoped>
.upload-zone {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  padding: 3rem 2rem;
  text-align: center;
  transition: all 0.3s ease;
  background: #f8f9fa;
}

.upload-zone.drag-over {
  border-color: #0d6efd;
  background: #e7f3ff;
}

.upload-placeholder i {
  font-size: 4rem;
  color: #6c757d;
  margin-bottom: 1rem;
}

.upload-placeholder p {
  margin: 0.5rem 0;
  font-size: 1.1rem;
}

.upload-file-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #fff;
  border-radius: 6px;
}

.upload-file-info i {
  font-size: 2rem;
  color: #0d6efd;
}

.file-details {
  flex: 1;
  text-align: left;
}

.file-name {
  margin: 0;
  font-weight: 600;
}

.file-size {
  margin: 0;
  font-size: 0.875rem;
}

.preview-content {
  margin-top: 1.5rem;
}

.preview-summary {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 6px;
}

.preview-summary h5 {
  margin-bottom: 1rem;
}

.preview-summary p {
  margin-bottom: 0.5rem;
}

.table th {
  background: #f8f9fa;
  font-weight: 600;
}

.table td {
  vertical-align: middle;
}

.add-course-label {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #495057;
}
</style>
